/**
 * QuestionarioAutomationForm — form NATIVO pra automação com trigger
 * `questionario_resposta` (BUG-V2-058 / Sub-fase 6 + Sprint 3.1 paridade).
 *
 * **Sprint 3.1 (2026-05-26):** ações reduzidas de 4 → 13 (paridade total
 * com WorkflowAutomationForm). Schema local de actions virou permissivo
 * (`z.array(z.unknown())`) — backend Zod é authoritative. AcoesEditor
 * shared garante shape válido em runtime via defaultActionForType.
 *
 * **Sub-fase 6 (2026-05-25):** form NATIVO (não wrapper) — substituiu wrapper
 * fino com seletores específicos de pergunta + operator + value.
 *
 * Backend usado: `/api/v1/automations` (POST/PUT). Mesma tabela `automations`.
 * `trigger_config_json` contém `templateId + perguntaCodigo + respostaCondicao`.
 * Engine `cross-workflow-dispatch.ts` faz match (matchRespostaCondicao) E
 * REUSA `executeAction` do automations-engine (resolve cardId via
 * `runContext.parentCardId = event.cardId`) — paridade 13 ações nativa.
 */

import { useEffect, useMemo } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  Label,
  Select,
  Switch,
} from '@/components/ui';
import type {
  Automation,
  AutomationAction,
  CreateAutomationInput,
} from '@/lib/automations-store';
import {
  useQuestionarioTemplate,
  useQuestionarioPerguntas,
} from '@/lib/questionarios-store';

import type { AutomationContexto } from '../types';
import { AutomationDialogHeaderByContext } from '../AutomationDialogHeaderByContext';
import { AcoesEditor } from '../AcoesEditor';

// ─── Zod schemas ────────────────────────────────────────────────────────

const respostaCondicaoSchema = z.discriminatedUnion('operator', [
  z.object({ operator: z.literal('any') }).strict(),
  z.object({
    operator: z.literal('equals'),
    value: z.string().trim().min(1, 'Valor obrigatório pra "igual a"'),
  }).strict(),
  z.object({
    operator: z.literal('contains'),
    value: z.string().trim().min(1, 'Valor obrigatório pra "contém"'),
  }).strict(),
  z.object({ operator: z.literal('is_empty') }).strict(),
]);

// Sprint 3.1: schema permissivo pra actions — AcoesEditor garante shape via
// defaultActionForType; backend Zod (server/routes/automations.ts) faz a
// validação authoritative com discriminated union completa dos 13 tipos.
// `passthrough` permite atravessar todos os campos por tipo sem listá-los.
const actionSchema = z
  .object({ type: z.string().min(1) })
  .passthrough();

const formSchema = z.object({
  nome: z.string().trim().min(3, 'Mínimo 3 caracteres').max(80),
  perguntaCodigo: z.string().min(1, 'Selecione a pergunta'),
  respostaCondicao: respostaCondicaoSchema,
  actions: z.array(actionSchema).min(1, 'Pelo menos 1 ação').max(20),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

// ─── Helpers ────────────────────────────────────────────────────────────

const OPERATOR_LABELS: Record<'any' | 'equals' | 'contains' | 'is_empty', string> = {
  any: 'Qualquer resposta dispara',
  equals: 'Igual a (match exato)',
  contains: 'Contém (substring, ignora maiúsculas)',
  is_empty: 'Vazia / não respondida',
};

function autoGenerateName(
  values: Partial<FormValues>,
  perguntaTexto: string | undefined,
): string {
  const pergunta = perguntaTexto ?? values.perguntaCodigo ?? '?';
  const operator = values.respostaCondicao?.operator;
  let when = `Quando ${pergunta} for respondida`;
  if (operator === 'equals' && 'value' in (values.respostaCondicao ?? {})) {
    when = `Quando ${pergunta} = "${(values.respostaCondicao as { value: string }).value}"`;
  } else if (operator === 'contains' && 'value' in (values.respostaCondicao ?? {})) {
    when = `Quando ${pergunta} contém "${(values.respostaCondicao as { value: string }).value}"`;
  } else if (operator === 'is_empty') {
    when = `Quando ${pergunta} ficar vazia`;
  }
  // Sprint 3.1: 13 action types — usa label genérico pro nome auto.
  const action = values.actions?.[0] as { type?: string } | undefined;
  const then = action?.type ? `→ ${action.type}` : '';
  return then ? `${when}, ${then}`.slice(0, 80) : when.slice(0, 80);
}

// ─── Component ──────────────────────────────────────────────────────────

export interface QuestionarioAutomationFormProps {
  open: boolean;
  onClose: () => void;
  /** Submit handler — recebe input completo (sem workflowSlug, que é injetado
   * pelo caller via shell). */
  onSubmit: (input: Omit<CreateAutomationInput, 'workflowSlug'>) => Promise<void> | void;
  /** Automação existente em edit mode. */
  initial?: Automation;
  /** Contexto kind='questionario'. */
  contexto: Extract<AutomationContexto, { kind: 'questionario' }>;
}

export function QuestionarioAutomationForm({
  open,
  onClose,
  onSubmit,
  initial,
  contexto,
}: QuestionarioAutomationFormProps) {
  const templateQuery = useQuestionarioTemplate(contexto.templateId);
  const perguntasQuery = useQuestionarioPerguntas(contexto.templateId);
  const perguntas = perguntasQuery.data ?? [];

  // Pre-fill defaultValues a partir do initial (edit) ou contexto (create).
  const defaultValues: FormValues = useMemo(() => {
    if (initial && initial.trigger.type === 'questionario_resposta') {
      const cfg = initial.trigger.config;
      return {
        nome: initial.nome,
        perguntaCodigo: cfg.perguntaCodigo ?? contexto.perguntaCodigo ?? '',
        respostaCondicao: cfg.respostaCondicao ?? { operator: 'any' },
        actions: initial.actions as unknown as FormValues['actions'],
        enabled: initial.enabled,
      };
    }
    return {
      nome: '',
      perguntaCodigo: contexto.perguntaCodigo ?? '',
      respostaCondicao: { operator: 'any' },
      actions: [
        {
          type: 'notify_in_app',
          target: 'responsavel',
          message: 'Resposta recebida em {{cliente}}',
        },
      ],
      enabled: true,
    };
  }, [initial, contexto.perguntaCodigo]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues,
    mode: 'onChange',
  });
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  // Reset ao abrir/fechar (preserva pattern do WorkflowAutomationForm).
  useEffect(() => {
    if (open) reset(defaultValues);
  }, [open, defaultValues, reset]);

  // Watch pra auto-gerar nome dinamicamente.
  const watchedValues = watch();
  const selectedPergunta = perguntas.find(
    (p) => p.codigo === watchedValues.perguntaCodigo,
  );
  const autoName = autoGenerateName(watchedValues, selectedPergunta?.pergunta);

  const operatorValue = watchedValues.respostaCondicao?.operator ?? 'any';
  const needsValueInput = operatorValue === 'equals' || operatorValue === 'contains';

  async function submitHandler(values: FormValues) {
    // Build trigger config (templateId vem do contexto, não do form).
    const triggerConfig: NonNullable<Automation['trigger']['config']> = {
      templateId: contexto.templateId,
      perguntaCodigo: values.perguntaCodigo,
      respostaCondicao: values.respostaCondicao,
    };
    await onSubmit({
      nome: values.nome.trim() || autoName,
      trigger: { type: 'questionario_resposta', config: triggerConfig },
      actions: values.actions as unknown as AutomationAction[],
      enabled: values.enabled,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <AutomationDialogHeaderByContext
          contexto={contexto}
          isEditing={!!initial}
          editingNome={initial?.nome}
        />
      </DialogHeader>
      <form onSubmit={handleSubmit(submitHandler)} data-testid="questionario-automation-form">
        <DialogContent>
          {templateQuery.isLoading && (
            <p
              data-testid="questionario-form-loading-template"
              className="text-sm italic text-ink-3 dark:text-ink-dark-3"
            >
              Carregando template…
            </p>
          )}
          {templateQuery.isError && (
            <Alert variant="danger" rail data-testid="questionario-form-error-template">
              Erro ao carregar template. Tente novamente.
            </Alert>
          )}

          <div className="space-y-5">
            {/* ─── Seção Quando ───────────────────────────────────── */}
            <section data-testid="questionario-form-quando" className="space-y-3">
              <h3 className="font-semibold text-base text-ink-1 dark:text-ink-dark-1">
                Quando
              </h3>
              <div>
                <Label htmlFor="qauto-pergunta" required>
                  Quando esta pergunta for respondida
                </Label>
                <Select
                  id="qauto-pergunta"
                  data-testid="qauto-pergunta"
                  {...register('perguntaCodigo')}
                  error={errors.perguntaCodigo?.message}
                >
                  <option value="">Selecione…</option>
                  {perguntas.map((p) => (
                    <option key={p.id} value={p.codigo}>
                      {p.codigo} — {p.pergunta}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label htmlFor="qauto-operator" required>
                  E a resposta for
                </Label>
                <Select
                  id="qauto-operator"
                  data-testid="qauto-operator"
                  value={operatorValue}
                  onChange={(e) => {
                    const op = e.target.value as 'any' | 'equals' | 'contains' | 'is_empty';
                    // Switch operator → reset value se inaplicável.
                    if (op === 'any' || op === 'is_empty') {
                      setValue('respostaCondicao', { operator: op });
                    } else {
                      setValue('respostaCondicao', { operator: op, value: '' });
                    }
                  }}
                >
                  {(Object.keys(OPERATOR_LABELS) as Array<keyof typeof OPERATOR_LABELS>).map(
                    (op) => (
                      <option key={op} value={op}>
                        {OPERATOR_LABELS[op]}
                      </option>
                    ),
                  )}
                </Select>
              </div>

              {needsValueInput && (
                <div data-testid="qauto-value-field">
                  <Label htmlFor="qauto-value" required>
                    Valor da resposta
                  </Label>
                  <Input
                    id="qauto-value"
                    data-testid="qauto-value"
                    placeholder={operatorValue === 'equals' ? 'Ex: Sim' : 'Ex: vazamento'}
                    {...register('respostaCondicao.value' as 'respostaCondicao')}
                  />
                </div>
              )}
            </section>

            {/* ─── Seção Então (Sprint 3.1: AcoesEditor com 13 ações) ───────────── */}
            <section data-testid="questionario-form-entao" className="space-y-3">
              <h3 className="font-semibold text-base text-ink-1 dark:text-ink-dark-1">
                Então
              </h3>
              {/* Sprint 3.1: nota "Pra mais opções edite no admin" REMOVIDA — paridade total. */}
              <AcoesEditor
                form={form}
                name="actions"
                // Sem columns — questionario não tem coluna kanban nativa.
                // move_card ainda aparece e move o card host (deal/OS) resolvido
                // pelo engine cross-workflow-dispatch via runContext.parentCardId.
                columns={[]}
              />
            </section>

            {/* ─── Seção Identificação ────────────────────────────── */}
            <section data-testid="questionario-form-identificacao" className="space-y-3">
              <h3 className="font-semibold text-base text-ink-1 dark:text-ink-dark-1">
                Identificação
              </h3>
              <div>
                <Label htmlFor="qauto-nome" required>
                  Nome da automação
                </Label>
                <Input
                  id="qauto-nome"
                  data-testid="qauto-nome"
                  placeholder={autoName || 'Nome único e descritivo'}
                  {...register('nome')}
                  error={errors.nome?.message}
                />
                {!watchedValues.nome && autoName && (
                  <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                    Sugestão: <em>{autoName}</em>
                  </p>
                )}
              </div>
              <Switch
                label="Ativa imediatamente"
                checked={watchedValues.enabled ?? true}
                onChange={(e) => setValue('enabled', e.target.checked)}
              />
            </section>
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" loading={isSubmitting} data-testid="qauto-submit">
            {initial ? 'Salvar' : 'Criar automação'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
